<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  showAccept: {
    type: Boolean,
    default: false,
  },
  showAcceptedFeedback: {
    type: Boolean,
    default: false,
  },
  showDecline: {
    type: Boolean,
    default: false,
  },
  showDeclinedFeedback: {
    type: Boolean,
    default: false,
  },
  showCreateSuccess: {
    type: Boolean,
    default: false,
  },
  selectedOffer: {
    type: Object,
    default: null,
  },
  createdOffer: {
    type: Object,
    default: null,
  },
  acceptModalTitle: {
    type: String,
    required: true,
  },
  acceptedFeedbackDescription: {
    type: String,
    required: true,
  },
  declinedFeedbackDescription: {
    type: String,
    required: true,
  },
  acceptValueRows: {
    type: Array,
    required: true,
  },
  declineForm: {
    type: Object,
    required: true,
  },
  declineReasonOptions: {
    type: Array,
    required: true,
  },
  declineSubmitLabel: {
    type: String,
    required: true,
  },
  declineNotes: {
    type: String,
    required: true,
  },
  offerAction: {
    type: String,
    default: '',
  },
  offerSummary: {
    type: Function,
    required: true,
  },
  formatBRL: {
    type: Function,
    required: true,
  },
  kindLabel: {
    type: Function,
    required: true,
  },
  contextLabel: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'closeAccept',
  'accept',
  'closeAcceptedFeedback',
  'showAcceptedOffers',
  'closeDecline',
  'decline',
  'closeDeclinedFeedback',
  'showCanceledOffers',
  'closeCreateSuccess',
  'openCreatedOfferDetails',
  'update:declineForm',
]);

const updateDeclineForm = (field, value) => {
  emit('update:declineForm', {
    ...props.declineForm,
    [field]: value,
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="showAccept"
    :title="acceptModalTitle"
    max-width-class="max-w-[680px]"
    @close="emit('closeAccept')"
  >
    <div v-if="selectedOffer" class="grid gap-4">
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
      >
        <p class="font-semibold text-lais-text">
          Proposta para fechar contrato
        </p>
        <p class="mt-1">
          Confira os valores antes de aceitar. Para acompanhar os próximos
          passos, acesse a área de propostas aceitas.
        </p>
      </div>

      <div class="overflow-x-auto rounded-lais border border-lais-border">
        <table class="min-w-[520px] w-full text-[13px]">
          <thead>
            <tr class="border-b border-lais-border-soft text-left">
              <th class="px-3 py-2 font-semibold text-lais-text">Valores</th>
              <th class="px-3 py-2 font-semibold text-lais-text">
                {{
                  offerSummary(selectedOffer).property_label ||
                  'Imóvel anunciado'
                }}
              </th>
              <th class="px-3 py-2 font-semibold text-lais-text">
                {{ offerSummary(selectedOffer).offer_label || 'Proposta' }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in acceptValueRows"
              :key="`accept-${row.label}`"
              class="border-b border-lais-border-soft last:border-0"
              :class="row.highlight ? 'bg-lais-primary-50/40' : ''"
            >
              <td class="px-3 py-2 font-semibold text-lais-text">
                {{ row.label }}
              </td>
              <td class="px-3 py-2 text-lais-text-body">
                {{ formatBRL(row.advertised) }}
              </td>
              <td class="px-3 py-2 font-semibold text-lais-text">
                {{ formatBRL(row.proposal) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('closeAccept')">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-check"
          :is-loading="offerAction === 'accept'"
          @click="emit('accept')"
        >
          Aceitar proposta
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisModal
    :show="showAcceptedFeedback"
    title="Proposta aceita"
    max-width-class="max-w-[520px]"
    @close="emit('closeAcceptedFeedback')"
  >
    <div class="grid gap-3 text-[13px] leading-relaxed text-lais-text-body">
      <span
        class="i-lucide-badge-check size-9 text-lais-primary"
        aria-hidden="true"
      />
      <p>{{ acceptedFeedbackDescription }}</p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          @click="emit('closeAcceptedFeedback')"
        >
          Certo, entendi
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-list-checks"
          @click="emit('showAcceptedOffers')"
        >
          Ver propostas aceitas
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisModal
    :show="showDecline"
    title="Recusar proposta"
    max-width-class="max-w-[620px]"
    @close="emit('closeDecline')"
  >
    <div class="grid gap-4">
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
      >
        <p class="font-semibold text-lais-text">
          Escolha o motivo de ter recusado
        </p>
        <p class="mt-1 text-lais-text-helper">
          A proposta será marcada como cancelada e o motivo ficará no histórico.
        </p>
      </div>

      <label class="grid gap-1">
        <span
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Motivo
        </span>
        <select
          :value="declineForm.reason"
          class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @change="updateDeclineForm('reason', $event.target.value)"
        >
          <option
            v-for="option in declineReasonOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <label
        class="flex items-start gap-3 rounded-lais border border-lais-border bg-white px-3 py-3 text-[13px] text-lais-text"
      >
        <input
          type="checkbox"
          :checked="declineForm.disable_listing"
          class="mt-0.5 size-4 accent-lais-primary"
          @change="updateDeclineForm('disable_listing', $event.target.checked)"
        />
        <span>
          <strong class="block">O anúncio será desativado</strong>
          <span class="mt-1 block text-lais-text-muted">
            Ao selecionar essa opção, o anúncio não vai mais aparecer para quem
            está buscando imóveis. Visitas e propostas em andamento serão
            canceladas.
          </span>
        </span>
      </label>

      <label class="grid gap-1">
        <span
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Observação
        </span>
        <textarea
          :value="declineForm.notes"
          rows="3"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
          placeholder="Inclua detalhes para o histórico, se necessário"
          @input="updateDeclineForm('notes', $event.target.value)"
        />
      </label>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('closeDecline')">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-x"
          :is-loading="offerAction === 'decline'"
          @click="emit('decline', declineNotes)"
        >
          {{ declineSubmitLabel }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisModal
    :show="showDeclinedFeedback"
    title="Proposta recusada"
    max-width-class="max-w-[540px]"
    @close="emit('closeDeclinedFeedback')"
  >
    <div class="grid gap-3 text-[13px] leading-relaxed text-lais-text-body">
      <span
        class="i-lucide-circle-x size-9 text-lais-primary"
        aria-hidden="true"
      />
      <p>{{ declinedFeedbackDescription }}</p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          @click="emit('closeDeclinedFeedback')"
        >
          Certo, entendi
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-list-x"
          @click="emit('showCanceledOffers')"
        >
          Ver propostas canceladas
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisModal
    :show="showCreateSuccess"
    title="Proposta criada com sucesso"
    max-width-class="max-w-[560px]"
    @close="emit('closeCreateSuccess')"
  >
    <div class="grid gap-4 text-[13px] leading-relaxed text-lais-text-body">
      <span
        class="i-lucide-file-check-2 size-9 text-lais-primary"
        aria-hidden="true"
      />
      <p>
        Quando você tiver propostas, elas aparecerão aqui para que você possa
        avaliar e acompanhar.
      </p>
      <div
        v-if="createdOffer"
        class="rounded-lais border border-lais-border bg-lais-bg-soft p-3"
      >
        <p class="font-semibold text-lais-text">
          {{ createdOffer.imovel_titulo || 'Imóvel não informado' }}
        </p>
        <p class="mt-1">
          {{ kindLabel(createdOffer.kind) }} ·
          {{ contextLabel(createdOffer.business_context) }} ·
          {{ formatBRL(createdOffer.offer_amount) }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('closeCreateSuccess')">
          Certo, entendi
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-list-checks"
          @click="emit('openCreatedOfferDetails')"
        >
          Exibir detalhes
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
